import { useState, useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import Vditor from 'vditor';
import 'vditor/dist/index.css';

interface File {
  id: string;
  name: string;
  path: string;
  content: string;
  size: number;
  git_status?: string;
  created_at: number;
  updated_at: number;
}

interface Project {
  id: string;
  name: string;
}

export default function FileEditor() {
  const { projectId, fileId } = useParams<{ projectId: string, fileId: string }>();
  const [file, setFile] = useState<File | null>(null);
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const vditorRef = useRef<any>(null);
  const editorRef = useRef<Vditor | null>(null);
  const saveTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    if (projectId && fileId) {
      fetchProject();
      fetchFile();
    }
    
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      if (editorRef.current) {
        editorRef.current.destroy();
      }
    };
  }, [projectId, fileId]);

  const fetchProject = async () => {
    try {
      const response = await axios.get(`/api/projects/${projectId}`);
      setProject(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching project:', err);
      setError('Failed to load project');
    }
  };

  const fetchFile = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/projects/${projectId}/files/${fileId}`);
      setFile(response.data);
      
      // Initialize editor after getting file content
      initEditor(response.data.content);
      
      setError(null);
    } catch (err) {
      console.error('Error fetching file:', err);
      setError('Failed to load file');
    } finally {
      setLoading(false);
    }
  };

  const initEditor = (content: string) => {
    if (vditorRef.current) {
      if (editorRef.current) {
        editorRef.current.destroy();
      }
      
      editorRef.current = new Vditor(vditorRef.current, {
        height: 'calc(100vh - 200px)',
        mode: 'ir',
        value: content,
        theme: 'classic',
        cache: {
          enable: false
        },
        toolbar: [
          'headings', 'bold', 'italic', 'strike', 'line', 'quote', 'list', 'ordered-list', 
          'check', 'code', 'inline-code', 'link', 'table', 'undo', 'redo', 'fullscreen'
        ],
        after: () => {
          // Setup auto-save
          editorRef.current?.vditor.element.addEventListener('input', () => {
            if (saveTimeoutRef.current) {
              clearTimeout(saveTimeoutRef.current);
            }
            
            saveTimeoutRef.current = window.setTimeout(() => {
              saveFile();
            }, 2000); // Auto-save after 2 seconds of inactivity
          });
        }
      });
    }
  };

  const saveFile = async () => {
    if (!editorRef.current) return;
    
    const content = editorRef.current.getValue();
    
    try {
      setSaving(true);
      await axios.put(`/api/projects/${projectId}/files/${fileId}`, {
        content
      });
      
      setLastSaved(new Date());
      setError(null);
    } catch (err) {
      console.error('Error saving file:', err);
      setError('Failed to save file');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !file) {
    return <div className="p-4">Loading file...</div>;
  }

  return (
    <div className="p-4">
      <div className="mb-4 flex justify-between items-center">
        <div>
          <Link to={`/projects/${projectId}`} className="text-blue-500 hover:underline">
            ← Back to {project?.name || 'Project'} Files
          </Link>
        </div>
        <div className="flex items-center gap-2">
          {saving && (
            <span className="text-yellow-500 text-sm">Saving...</span>
          )}
          {lastSaved && !saving && (
            <span className="text-green-500 text-sm">
              Saved at {lastSaved.toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={saveFile}
            disabled={saving || !editorRef.current}
            className="bg-blue-500 text-white px-4 py-2 rounded disabled:bg-blue-300"
          >
            Save
          </button>
        </div>
      </div>
      
      <h1 className="text-2xl font-bold mb-4">
        {file?.name || 'File'}
      </h1>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      
      <div className="border rounded">
        <div ref={vditorRef}></div>
      </div>
    </div>
  );
}
